import configureStore from "@client/app/store";
import { initGlobals } from "@client/globals";
import { processInIframe } from "@client/lib/misc";
import { processQueryParameters } from "@client/lib/url";
import { getScreenType } from "@client/lib/viewport";
import { type AppState, type PathsAppState, type SiteInfoAppState } from "@client/types";
import { type Api, type InitResponseData } from "@client/types/Api";
import { type AppInfo } from "@common/declarations";
import { initCommon } from "@common/index";
import { toMap } from "@common/lib/array";
import { type I18n } from "@common/lib/i18n";
import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import type { AnyAction, Store } from "redux";
import { type ThunkActionDispatch } from "redux-thunk";
import { init, initSsr } from "./actions";
import AiChat from "./AiChat";
import Router from "./Router/component";
import View from "./View";

import "@client/styles/common.less";
import "./style.less";

const renderView = (store: Store<AppState, AnyAction>) => (
  <Router>
    <View store={store}>
      <AiChat />
    </View>
  </Router>
);

type InitBase = {
  i18n: I18n;
  info: AppInfo;
  labels: Api.Labels[];
  paths: PathsAppState;
  siteInfo: SiteInfoAppState;
  version: string;
};
const initBase = (args: InitBase) => {
  initGlobals({
    mode: "app",
    paths: args.paths,
    siteInfo: args.siteInfo,
    version: args.version
  });

  initCommon(args.info, args.i18n);
};

export type InitBrowser = InitBase & {
  data: InitResponseData;
  state: Partial<AppState>;
};
export const browser = (container: HTMLElement, args: InitBrowser) => {
  initBase(args);

  const store = configureStore({
    ...args.state,
    cultureCode: args.info.cultureCode,
    isEditMode: false,
    labels: toMap(
      args.labels,
      (x) => x.category,
      (x) => x.i18n
    )
  });

  if (module["hot"]) {
    module["hot"].accept("./View.tsx", () => {
      root.render(renderView(store));
    });
  }

  const root = createRoot(container);
  root.render(renderView(store));

  (store.dispatch as ThunkActionDispatch<any>)(init(args.data));

  processInIframe();
  processQueryParameters();
};

export const server = (args: InitBrowser) => {
  initBase(args);

  return configureStore({
    ...args.state,

    isEditMode: false
  });
};

type InitSsr = InitBase & {
  state: string;
};
export const ssr = (container: HTMLElement, args: InitSsr) => {
  const state: AppState = JSON.parse(args.state);
  state.viewport = {
    fixedScreen: null,
    screen: getScreenType(window.innerWidth)
  };
  initBase(args);

  const store = configureStore({
    ...state,
    cultureCode: args.info.cultureCode,
    isEditMode: false,
    labels: toMap(
      args.labels,
      (x) => x.category,
      (x) => x.i18n
    )
  });

  hydrateRoot(container, renderView(store), {
    onRecoverableError: (error, errorInfo) => {
      console.error("Uncaught error", error, errorInfo.componentStack);
    }
  });

  (store.dispatch as ThunkActionDispatch<any>)(initSsr());

  processQueryParameters();
};
